import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const DigitalTwin = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [twinData, setTwinData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [simulationParams, setSimulationParams] = useState({
    budget_change: 0,
    conversion_rate_change: 0,
    traffic_change: 0,
    cac_change: 0,
    clv_change: 0,
    cpc_change: 0,
    email_performance: 0,
    social_performance: 0,
    paid_ads_performance: 0,
    timeframe_months: 3
  });
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [timeRange, setTimeRange] = useState('30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [regionComparison, setRegionComparison] = useState(false);

  useEffect(() => {
    fetchDigitalTwin();
  }, [companyId, timeRange, customStartDate, customEndDate, selectedRegion]);

  const fetchDigitalTwin = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `/companies/${companyId}/digital-twin`;
      const params = new URLSearchParams();
      
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', customStartDate);
        params.append('end_date', customEndDate);
      } else if (timeRange === '30_days') {
        params.append('days', '30');
      } else if (timeRange === 'one_year') {
        params.append('days', '365');
      }
      
      if (selectedRegion && selectedRegion !== 'all') {
        params.append('region', selectedRegion);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      
      // Add cost data to time_series if missing
      if (response.data.time_series) {
        response.data.time_series = response.data.time_series.map(item => ({
          ...item,
          cost: item.cost || (item.revenue * 0.4) // Simulate cost as 40% of revenue if missing
        }));
      }
      
      setTwinData(response.data);
    } catch (err) {
      console.error('Error fetching digital twin:', err);
      setError(err.response?.data?.error || 'Failed to load digital twin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSimulate = async () => {
    try {
      setSimulating(true);
      const response = await api.post(`/companies/${companyId}/digital-twin/simulate`, simulationParams);
      setSimulationResult(response.data);
    } catch (err) {
      console.error('Error simulating scenario:', err);
      alert('Failed to run simulation: ' + (err.response?.data?.error || err.message));
    } finally {
      setSimulating(false);
    }
  };

  const resetSimulation = () => {
    setSimulationParams({
      budget_change: 0,
      conversion_rate_change: 0,
      traffic_change: 0,
      cac_change: 0,
      clv_change: 0,
      cpc_change: 0,
      email_performance: 0,
      social_performance: 0,
      paid_ads_performance: 0,
      timeframe_months: 3
    });
    setSimulationResult(null);
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getImpactBadgeColor = (impact) => {
    switch(impact) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Theme-aware card style
  const cardStyle = {
    background: isDark ? '#1f2937' : 'white',
    border: `1px solid ${isDark ? 'var(--gray-700)' : 'var(--gray-200)'}`,
    borderRadius: '8px',
    padding: 'var(--space-4)',
    boxShadow: isDark ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)'
  };

  const cardStyleLarge = {
    ...cardStyle,
    padding: 'var(--space-5)'
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading Digital Twin...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>Error Loading Digital Twin</h3>
          <p>{error}</p>
          <button onClick={fetchDigitalTwin} className="btn btn-primary">Retry</button>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">Go Back</button>
        </div>
      </div>
    );
  }

  if (!twinData) {
    return <div className="error-container"><p>No data available</p></div>;
  }

  const { 
    company = {}, 
    overview = {}, 
    financial = {}, 
    performance = {}, 
    customer_segments = [], 
    channel_performance = [], 
    time_series = [], 
    predictions = {}, 
    recommendations = [], 
    conversion_funnel = [], 
    health_score = 0 
  } = twinData;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <button
          onClick={() => navigate('/companies')}
          className="breadcrumb-link"
        >
          Companies
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {company.name || 'Company'} - Digital Twin
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🤖 {company.name || 'Company'} Digital Twin
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              AI-Powered Marketing Ecosystem Intelligence
            </p>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            borderRadius: '12px',
            backgroundColor: getHealthScoreColor(health_score),
            color: 'white',
            minWidth: '120px'
          }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', lineHeight: 1 }}>
              {health_score}
            </div>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.25rem' }}>
              Health Score
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '0',
            gap: 'var(--space-1)'
          }}>
            {[
              { key: 'overview', label: '📊 Overview' },
              { key: 'analytics', label: '📈 Analytics' },
              { key: 'predictions', label: '🔮 AI Predictions' },
              { key: 'simulator', label: '🎮 Scenario Simulator' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'none',
                  border: 'none',
                  borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-500)',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Key Metrics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 'var(--space-4)',
              marginBottom: 'var(--space-6)'
            }}>
              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                  Projects
                </div>
                <div style={{ color: 'var(--gray-900)', fontSize: '1.75rem', fontWeight: 'bold' }}>
                  {overview.total_projects || 0}
                </div>
              </div>
              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                  Active Campaigns
                </div>
                <div style={{ color: 'var(--gray-900)', fontSize: '1.75rem', fontWeight: 'bold' }}>
                  {overview.active_campaigns || 0}
                </div>
              </div>
              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                  Total Leads
                </div>
                <div style={{ color: 'var(--gray-900)', fontSize: '1.75rem', fontWeight: 'bold' }}>
                  {overview.total_leads || 0}
                </div>
              </div>
              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                  Customers
                </div>
                <div style={{ color: 'var(--gray-900)', fontSize: '1.75rem', fontWeight: 'bold' }}>
                  {overview.total_customers || 0}
                </div>
              </div>
              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                  Revenue
                </div>
                <div style={{ color: 'var(--gray-900)', fontSize: '1.75rem', fontWeight: 'bold' }}>
                  ${(financial.total_revenue || 0).toLocaleString()}
                </div>
              </div>
              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                  ROI
                </div>
                <div style={{ color: (financial.roi || 0) >= 200 ? '#10b981' : '#f59e0b', fontSize: '1.75rem', fontWeight: 'bold' }}>
                  {financial.roi || 0}%
                </div>
              </div>
            </div>

            {/* Key Performance Indicators */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'var(--gray-900)',
                marginBottom: 'var(--space-4)'
              }}>
                Key Performance Indicators
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'var(--space-4)'
              }}>
                <div style={{
                  background: isDark ? '#1f2937' : 'white',
                  border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: 'var(--space-4)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                    Cost Per Acquisition (CPA)
                  </div>
                  <div style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    ${(performance.customer_acquisition_cost || 0).toLocaleString()}
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    Per customer
                  </div>
                </div>

                <div style={{
                  background: isDark ? '#1f2937' : 'white',
                  border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: 'var(--space-4)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                    Cost Per Lead (CPL)
                  </div>
                  <div style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    ${(performance.cost_per_lead || 0).toLocaleString()}
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    Per lead
                  </div>
                </div>

                <div style={{
                  background: isDark ? '#1f2937' : 'white',
                  border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: 'var(--space-4)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                    Cost Per Click (CPC)
                  </div>
                  <div style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    ${(performance.cost_per_click || 0).toFixed(2)}
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    Average
                  </div>
                </div>

                <div style={{
                  background: isDark ? '#1f2937' : 'white',
                  border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: 'var(--space-4)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                    Click-Through Rate (CTR)
                  </div>
                  <div style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {(performance.click_through_rate || 0).toFixed(2)}%
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    Average
                  </div>
                </div>

                <div style={{
                  background: isDark ? '#1f2937' : 'white',
                  border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: 'var(--space-4)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                    Conversion Rate
                  </div>
                  <div style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {(performance.conversion_rate || 0).toFixed(2)}%
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    Leads to customers
                  </div>
                </div>

                <div style={{
                  background: isDark ? '#1f2937' : 'white',
                  border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: 'var(--space-4)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginBottom: 'var(--space-2)' }}>
                    Customer Lifetime Value (CLV)
                  </div>
                  <div style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    ${(performance.customer_lifetime_value || 0).toLocaleString()}
                  </div>
                  <div style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginTop: 'var(--space-1)' }}>
                    Average
                  </div>
                </div>
              </div>
            </div>

            {/* AI Recommendations */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'var(--gray-900)',
                marginBottom: 'var(--space-4)'
              }}>
                AI Recommendations
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {(recommendations || []).map((rec, index) => (
                  <div key={index} style={{
                    background: isDark ? '#1f2937' : 'white',
                    border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    padding: 'var(--space-4)',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                      <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>
                        {rec.title}
                      </span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        color: 'white',
                        backgroundColor: getImpactBadgeColor(rec.impact)
                      }}>
                        {rec.impact} impact
                      </span>
                    </div>
                    <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', margin: 0 }}>
                      {rec.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Segments */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: 'var(--space-6)',
              marginBottom: 'var(--space-6)'
            }}>
              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-5)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: 'bold',
                  color: 'var(--gray-900)',
                  margin: '0 0 var(--space-4) 0'
                }}>
                  Customer Segments
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={customer_segments}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name} (${percentage}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {(customer_segments || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: 'var(--space-5)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: 'bold',
                  color: 'var(--gray-900)',
                  margin: '0 0 var(--space-4) 0'
                }}>
                  Channel Performance
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={channel_performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="channel" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="leads" fill="#3b82f6" />
                    <Bar dataKey="conversions" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Detailed Channel Analysis */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'var(--gray-900)',
                marginBottom: 'var(--space-4)'
              }}>
                Detailed Channel Analysis
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 'var(--space-4)'
              }}>
                {(channel_performance || []).map((channel, index) => {
                  const channelROI = channel.revenue > 0 && channel.cost > 0 
                    ? (((channel.revenue - channel.cost) / channel.cost) * 100).toFixed(1)
                    : 'N/A';
                  const conversionRate = channel.leads > 0 
                    ? ((channel.conversions / channel.leads) * 100).toFixed(2)
                    : 0;
                  const cpl = channel.leads > 0 && channel.cost > 0
                    ? (channel.cost / channel.leads).toFixed(2)
                    : 0;
                  const cpa = channel.conversions > 0 && channel.cost > 0
                    ? (channel.cost / channel.conversions).toFixed(2)
                    : 0;

                  return (
                    <div key={index} style={{
                      background: isDark ? '#1f2937' : 'white',
                      border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                      borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
                      borderRadius: '8px',
                      padding: 'var(--space-4)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 'var(--space-3)'
                      }}>
                        <h4 style={{
                          fontSize: '1.125rem',
                          fontWeight: 'bold',
                          color: 'var(--gray-900)',
                          margin: 0
                        }}>
                          {channel.channel}
                        </h4>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: COLORS[index % COLORS.length] + '20',
                          color: COLORS[index % COLORS.length]
                        }}>
                          {channelROI !== 'N/A' ? `${channelROI}% ROI` : 'N/A'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>Impressions:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            {(channel.impressions || 0).toLocaleString()}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>Clicks:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            {(channel.clicks || 0).toLocaleString()}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>CTR:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            {channel.ctr || 0}%
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>Leads:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            {(channel.leads || 0).toLocaleString()}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>Conversions:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            {(channel.conversions || 0).toLocaleString()}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>Conversion Rate:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            {conversionRate}%
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>Cost:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            ${(channel.cost || 0).toLocaleString()}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>Revenue:</span>
                          <span style={{ color: '#10b981', fontWeight: '600', fontSize: '0.875rem' }}>
                            ${(channel.revenue || 0).toLocaleString()}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--gray-100)' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>CPL:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            ${cpl}
                          </span>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                          <span style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>CPA:</span>
                          <span style={{ color: 'var(--gray-900)', fontWeight: '600', fontSize: '0.875rem' }}>
                            ${cpa}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {/* Time Series */}
            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-5)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              marginBottom: 'var(--space-6)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: 'bold',
                  color: 'var(--gray-900)',
                  margin: 0
                }}>
                  Performance Trends
                </h3>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <select
                    value={timeRange}
                    onChange={(e) => {
                      setTimeRange(e.target.value);
                      if (e.target.value !== 'custom') {
                        setCustomStartDate('');
                        setCustomEndDate('');
                      }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: '1px solid var(--gray-300)',
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="30_days">30 Days</option>
                    <option value="one_year">One Year</option>
                    <option value="custom">Custom Range</option>
                  </select>
                  
                  {timeRange === 'custom' && (
                    <>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid var(--gray-300)',
                          fontSize: '0.875rem'
                        }}
                        placeholder="Start Date"
                      />
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid var(--gray-300)',
                          fontSize: '0.875rem'
                        }}
                        placeholder="End Date"
                      />
                    </>
                  )}
                </div>
              </div>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={time_series}>
                <defs>
                  <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorConversions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="leads" stroke="#3b82f6" fillOpacity={1} fill="url(#colorLeads)" />
                <Area type="monotone" dataKey="conversions" stroke="#10b981" fillOpacity={1} fill="url(#colorConversions)" />
                <Area type="monotone" dataKey="revenue" stroke="#f59e0b" fillOpacity={1} fill="url(#colorRevenue)" />
              </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Conversion Funnel */}
        <div style={{
          background: isDark ? '#1f2937' : 'white',
          border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
          borderRadius: '8px',
          padding: 'var(--space-5)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: 'var(--space-6)'
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: 'var(--gray-900)',
            margin: '0 0 var(--space-4) 0'
          }}>
            Conversion Funnel
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', padding: 'var(--space-4)' }}>
            {(conversion_funnel || []).map((stage, index) => (
              <div key={index} style={{
                background: 'linear-gradient(90deg, var(--primary-500) 0%, var(--primary-600) 100%)',
                color: 'white',
                padding: 'var(--space-3)',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: `${stage.percentage}%`,
                transition: 'transform 0.2s'
              }}>
                <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                  {stage.stage}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {stage.count.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                  {stage.percentage}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue by Channel */}
        <div style={{
          background: isDark ? '#1f2937' : 'white',
          border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
          borderRadius: '8px',
          padding: 'var(--space-5)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: 'var(--space-6)'
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: 'var(--gray-900)',
            margin: '0 0 var(--space-4) 0'
          }}>
            Revenue by Channel
          </h3>
          {(channel_performance || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={channel_performance}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ channel, revenue }) => `${channel}: $${(revenue || 0).toLocaleString()}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="revenue"
                >
                  {(channel_performance || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-500)' }}>
              No revenue data available
            </div>
          )}
        </div>

        {/* Additional Analytics Charts */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: 'var(--space-6)',
          marginBottom: 'var(--space-6)'
        }}>
          {/* Lead Sources */}
          <div style={{
            background: isDark ? '#1f2937' : 'white',
            border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
            borderRadius: '8px',
            padding: 'var(--space-5)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 'bold',
              color: 'var(--gray-900)',
              margin: '0 0 var(--space-4) 0'
            }}>
              Lead Sources Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={twinData.lead_sources || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ source, percentage }) => `${source} (${percentage}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {(twinData.lead_sources || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Campaign ROI Comparison */}
          <div style={{
            background: isDark ? '#1f2937' : 'white',
            border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
            borderRadius: '8px',
            padding: 'var(--space-5)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 'bold',
              color: 'var(--gray-900)',
              margin: '0 0 var(--space-4) 0'
            }}>
              Campaign ROI Comparison
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={twinData.campaign_roi || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="campaign" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="roi" fill="#10b981" name="ROI %" />
                <Bar dataKey="budget" fill="#3b82f6" name="Budget" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Customer Acquisition Trends */}
        <div style={{
          background: isDark ? '#1f2937' : 'white',
          border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
          borderRadius: '8px',
          padding: 'var(--space-5)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: 'var(--space-6)'
        }}>
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: 'bold',
            color: 'var(--gray-900)',
            margin: '0 0 var(--space-4) 0'
          }}>
            Customer Acquisition & Cost Trends
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={time_series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Area yAxisId="left" type="monotone" dataKey="conversions" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Conversions" />
              <Area yAxisId="right" type="monotone" dataKey="cost" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="Cost" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}

        {/* Predictions Tab */}
        {activeTab === 'predictions' && (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--space-4)',
              marginBottom: 'var(--space-6)'
            }}>
            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-4)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>Next Month Revenue</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                ${(predictions.next_month_revenue || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: 'var(--space-1)' }}>↑ 15% from current</div>
            </div>

            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-4)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>Next Month Leads</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                {(predictions.next_month_leads || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: 'var(--space-1)' }}>↑ 10% from current</div>
            </div>

            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-4)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>Churn Risk</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#ef4444' }}>
                {(predictions.churn_risk || 0)}%
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: 'var(--space-1)' }}>Low risk level</div>
            </div>

            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-4)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>Growth Trend</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--gray-900)', textTransform: 'capitalize' }}>
                {predictions.growth_trend || 'Stable'}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#10b981', marginTop: 'var(--space-1)' }}>📈 Positive momentum</div>
            </div>

            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-4)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>Recommended Budget</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--gray-900)' }}>
                ${(predictions.recommended_budget || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: 'var(--space-1)' }}>+20% increase suggested</div>
            </div>

            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-4)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>Confidence Score</div>
              <div style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#10b981' }}>
                85%
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: 'var(--space-1)' }}>High confidence</div>
            </div>
          </div>

          {/* Revenue Forecast Chart */}
          <div style={{
            background: isDark ? '#1f2937' : 'white',
            border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
            borderRadius: '8px',
            padding: 'var(--space-5)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            marginBottom: 'var(--space-6)'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 'bold',
              color: 'var(--gray-900)',
              margin: '0 0 var(--space-4) 0'
            }}>
              90-Day Revenue Forecast
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={twinData.revenue_forecast || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="actual" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Actual Revenue" />
                <Area type="monotone" dataKey="predicted" stackId="2" stroke="#10b981" fill="#10b981" fillOpacity={0.4} name="Predicted Revenue" />
                <Area type="monotone" dataKey="best_case" stackId="3" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} name="Best Case" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Market Opportunities & Risk Analysis */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: 'var(--space-6)',
            marginBottom: 'var(--space-6)'
          }}>
            {/* Market Opportunities */}
            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-5)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: 'bold',
                color: 'var(--gray-900)',
                margin: '0 0 var(--space-4) 0'
              }}>
                🎯 Market Opportunities
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {(twinData.market_opportunities || []).map((opp, index) => (
                  <div key={index} style={{
                    padding: 'var(--space-3)',
                    background: 'var(--gray-50)',
                    borderRadius: '6px',
                    borderLeft: `4px solid ${opp.impact === 'high' ? '#10b981' : opp.impact === 'medium' ? '#f59e0b' : '#6b7280'}`
                  }}>
                    <div style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: 'var(--space-1)' }}>
                      {opp.title}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>
                      {opp.description}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Potential: {opp.potential}</span>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        backgroundColor: opp.impact === 'high' ? '#d1fae5' : opp.impact === 'medium' ? '#fef3c7' : '#f3f4f6',
                        color: opp.impact === 'high' ? '#065f46' : opp.impact === 'medium' ? '#92400e' : '#374151'
                      }}>
                        {opp.impact.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Factors */}
            <div style={{
              background: isDark ? '#1f2937' : 'white',
              border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
              borderRadius: '8px',
              padding: 'var(--space-5)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: 'bold',
                color: 'var(--gray-900)',
                margin: '0 0 var(--space-4) 0'
              }}>
                ⚠️ Risk Factors
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {(twinData.risk_factors || []).map((risk, index) => (
                  <div key={index} style={{
                    padding: 'var(--space-3)',
                    background: 'var(--gray-50)',
                    borderRadius: '6px',
                    borderLeft: `4px solid ${risk.severity === 'high' ? '#ef4444' : risk.severity === 'medium' ? '#f59e0b' : '#6b7280'}`
                  }}>
                    <div style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: 'var(--space-1)' }}>
                      {risk.title}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: 'var(--space-2)' }}>
                      {risk.description}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Mitigation: {risk.mitigation}</span>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.625rem',
                        fontWeight: '600',
                        backgroundColor: risk.severity === 'high' ? '#fee2e2' : risk.severity === 'medium' ? '#fef3c7' : '#f3f4f6',
                        color: risk.severity === 'high' ? '#991b1b' : risk.severity === 'medium' ? '#92400e' : '#374151'
                      }}>
                        {risk.severity.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* AI Insights */}
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: 'bold',
              color: 'var(--gray-900)',
              marginBottom: 'var(--space-4)'
            }}>
              AI-Powered Insights & Recommendations
            </h3>
            <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: 'var(--space-2)' }}>🎯 Opportunity Alert</div>
                <div style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Email campaigns show 40% higher conversion rates than average. Consider reallocating 15% of paid ads budget to email marketing for Q1 2026.
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: 'var(--space-2)' }}>⚡ Quick Win</div>
                <div style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Leads generated on Tuesdays and Wednesdays convert 25% better. Schedule high-value campaigns on these days for maximum impact.
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: 'var(--space-2)' }}>💡 Strategic Insight</div>
                <div style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Customer lifetime value is 3x higher for referral leads. Implement a referral program with incentives to accelerate growth.
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                borderRadius: '8px',
                padding: 'var(--space-4)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: 'var(--space-2)' }}>🚀 Growth Accelerator</div>
                <div style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                  Increase content production by 30% in underperforming segments. AI analysis suggests this could boost organic traffic by 45%.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Simulator Tab */}
      {activeTab === 'simulator' && (
        <div>
          {/* Region Selection */}
          <div style={{
            background: isDark ? '#1f2937' : 'white',
            border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
            borderRadius: '8px',
            padding: 'var(--space-4)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            marginBottom: 'var(--space-4)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)', marginBottom: 'var(--space-2)' }}>
                  🌍 Region Filter
                </label>
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="all">All Regions</option>
                  <option value="north_america">🇺🇸 North America</option>
                  <option value="europe">🇪🇺 Europe</option>
                  <option value="asia_pacific">🌏 Asia Pacific</option>
                  <option value="latin_america">🌎 Latin America</option>
                  <option value="middle_east">🕌 Middle East</option>
                  <option value="africa">🌍 Africa</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  id="regionComparison"
                  checked={regionComparison}
                  onChange={(e) => setRegionComparison(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <label htmlFor="regionComparison" style={{ fontSize: '0.875rem', color: 'var(--gray-700)', cursor: 'pointer' }}>
                  Enable Region Comparison
                </label>
              </div>
            </div>
          </div>

          {/* Scenario Simulator */}
          <div style={{
            background: isDark ? '#1f2937' : 'white',
            border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
            borderRadius: '8px',
            padding: 'var(--space-6)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: 'var(--gray-900)',
              margin: '0 0 var(--space-2) 0'
            }}>
              🎯 Advanced Scenario Simulator
            </h3>
            <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-6)' }}>
              Adjust multiple parameters to forecast how strategic changes would impact your marketing performance.
            </p>

            {/* Core Metrics */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>
                Core Metrics
              </h4>
              <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>💰 Budget Change</span>
                    <span style={{ color: simulationParams.budget_change >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {simulationParams.budget_change > 0 ? '+' : ''}{simulationParams.budget_change}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="200"
                    value={simulationParams.budget_change}
                    onChange={(e) => setSimulationParams({...simulationParams, budget_change: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>📈 Conversion Rate Change</span>
                    <span style={{ color: simulationParams.conversion_rate_change >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {simulationParams.conversion_rate_change > 0 ? '+' : ''}{simulationParams.conversion_rate_change}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="100"
                    value={simulationParams.conversion_rate_change}
                    onChange={(e) => setSimulationParams({...simulationParams, conversion_rate_change: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>👥 Traffic Change</span>
                    <span style={{ color: simulationParams.traffic_change >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {simulationParams.traffic_change > 0 ? '+' : ''}{simulationParams.traffic_change}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="200"
                    value={simulationParams.traffic_change}
                    onChange={(e) => setSimulationParams({...simulationParams, traffic_change: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>

            {/* Advanced Metrics */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>
                Cost & Value Optimization
              </h4>
              <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>🎯 CAC (Customer Acquisition Cost) Change</span>
                    <span style={{ color: simulationParams.cac_change >= 0 ? '#ef4444' : '#10b981', fontWeight: '600' }}>
                      {simulationParams.cac_change > 0 ? '+' : ''}{simulationParams.cac_change}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="100"
                    value={simulationParams.cac_change}
                    onChange={(e) => setSimulationParams({...simulationParams, cac_change: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>💎 CLV (Customer Lifetime Value) Change</span>
                    <span style={{ color: simulationParams.clv_change >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {simulationParams.clv_change > 0 ? '+' : ''}{simulationParams.clv_change}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="150"
                    value={simulationParams.clv_change}
                    onChange={(e) => setSimulationParams({...simulationParams, clv_change: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>🖱️ CPC (Cost Per Click) Change</span>
                    <span style={{ color: simulationParams.cpc_change >= 0 ? '#ef4444' : '#10b981', fontWeight: '600' }}>
                      {simulationParams.cpc_change > 0 ? '+' : ''}{simulationParams.cpc_change}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="100"
                    value={simulationParams.cpc_change}
                    onChange={(e) => setSimulationParams({...simulationParams, cpc_change: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>

            {/* Channel-Specific Adjustments */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>
                Channel-Specific Performance
              </h4>
              <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>📧 Email Marketing Performance</span>
                    <span style={{ color: simulationParams.email_performance >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {simulationParams.email_performance > 0 ? '+' : ''}{simulationParams.email_performance}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="150"
                    value={simulationParams.email_performance}
                    onChange={(e) => setSimulationParams({...simulationParams, email_performance: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>📱 Social Media Performance</span>
                    <span style={{ color: simulationParams.social_performance >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {simulationParams.social_performance > 0 ? '+' : ''}{simulationParams.social_performance}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="150"
                    value={simulationParams.social_performance}
                    onChange={(e) => setSimulationParams({...simulationParams, social_performance: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                    <span>💰 Paid Ads Performance</span>
                    <span style={{ color: simulationParams.paid_ads_performance >= 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                      {simulationParams.paid_ads_performance > 0 ? '+' : ''}{simulationParams.paid_ads_performance}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min="-50"
                    max="150"
                    value={simulationParams.paid_ads_performance}
                    onChange={(e) => setSimulationParams({...simulationParams, paid_ads_performance: parseInt(e.target.value)})}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                </div>
              </div>
            </div>

            {/* Timeframe Selection */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>
                Forecast Timeframe
              </h4>
              <div>
                <label style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-2)', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  <span>📅 Simulation Period</span>
                  <span style={{ fontWeight: '600', color: 'var(--gray-900)' }}>
                    {simulationParams.timeframe_months} {simulationParams.timeframe_months === 1 ? 'Month' : 'Months'}
                  </span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="24"
                  value={simulationParams.timeframe_months}
                  onChange={(e) => setSimulationParams({...simulationParams, timeframe_months: parseInt(e.target.value)})}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 'var(--space-1)' }}>
                  <span>1 month</span>
                  <span>6 months</span>
                  <span>12 months</span>
                  <span>24 months</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <button
                onClick={handleSimulate}
                disabled={simulating}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: simulating ? 'var(--gray-400)' : 'var(--primary-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: simulating ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
              >
                {simulating ? '⏳ Running Simulation...' : '🚀 Run Simulation'}
              </button>
              <button
                onClick={resetSimulation}
                style={{
                  padding: '0.75rem 2rem',
                  backgroundColor: 'white',
                  color: 'var(--gray-700)',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                🔄 Reset All
              </button>
            </div>

            {/* Results */}
            {simulationResult && (
              <div style={{ marginTop: 'var(--space-8)', paddingTop: 'var(--space-6)', borderTop: '2px solid var(--gray-200)' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--gray-900)', marginBottom: 'var(--space-6)' }}>
                  📊 Simulation Results
                </h3>
                
                {/* Key Metrics Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 'var(--space-4)',
                  marginBottom: 'var(--space-6)'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: 'var(--space-4)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: 'var(--space-1)' }}>Revenue</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      ${simulationResult.simulated.revenue.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.875rem', marginTop: 'var(--space-1)' }}>
                      {simulationResult.changes.revenue >= 0 ? '↑' : '↓'} {Math.abs(simulationResult.changes.revenue).toFixed(1)}%
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    padding: 'var(--space-4)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: 'var(--space-1)' }}>Leads</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {simulationResult.simulated.leads.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.875rem', marginTop: 'var(--space-1)' }}>
                      {simulationResult.changes.leads >= 0 ? '↑' : '↓'} {Math.abs(simulationResult.changes.leads).toFixed(1)}%
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    padding: 'var(--space-4)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: 'var(--space-1)' }}>Conversions</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {simulationResult.simulated.conversions.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.875rem', marginTop: 'var(--space-1)' }}>
                      {simulationResult.changes.conversions >= 0 ? '↑' : '↓'} {Math.abs(simulationResult.changes.conversions).toFixed(1)}%
                    </div>
                  </div>

                  <div style={{
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white',
                    padding: 'var(--space-4)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: 'var(--space-1)' }}>ROI</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                      {simulationResult.simulated.roi}%
                    </div>
                    <div style={{ fontSize: '0.875rem', marginTop: 'var(--space-1)' }}>
                      {simulationResult.changes.roi >= 0 ? '↑' : '↓'} {Math.abs(simulationResult.changes.roi).toFixed(1)}%
                    </div>
                  </div>
                </div>

                {/* Detailed Comparison */}
                <div style={{
                  background: 'var(--gray-50)',
                  padding: 'var(--space-5)',
                  borderRadius: '8px',
                  marginBottom: 'var(--space-6)'
                }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: 'var(--space-4)' }}>
                    Before vs After Comparison
                  </h4>
                  <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                    {[
                      { label: 'Revenue', current: simulationResult.current.revenue, simulated: simulationResult.simulated.revenue, format: 'currency' },
                      { label: 'Leads', current: simulationResult.current.leads, simulated: simulationResult.simulated.leads, format: 'number' },
                      { label: 'Conversions', current: simulationResult.current.conversions, simulated: simulationResult.simulated.conversions, format: 'number' },
                      { label: 'ROI', current: simulationResult.current.roi, simulated: simulationResult.simulated.roi, format: 'percent' }
                    ].map((metric, index) => (
                      <div key={index} style={{
                        display: 'grid',
                        gridTemplateColumns: '150px 1fr 80px 1fr',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-3)',
                        background: isDark ? '#1f2937' : 'white',
                        borderRadius: '6px'
                      }}>
                        <div style={{ fontWeight: '600', color: 'var(--gray-700)' }}>{metric.label}</div>
                        <div style={{ textAlign: 'right', color: 'var(--gray-600)' }}>
                          {metric.format === 'currency' ? `$${metric.current.toLocaleString()}` :
                           metric.format === 'percent' ? `${metric.current}%` :
                           metric.current.toLocaleString()}
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '1.25rem', color: 'var(--gray-400)' }}>→</div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)' }}>
                          {metric.format === 'currency' ? `$${metric.simulated.toLocaleString()}` :
                           metric.format === 'percent' ? `${metric.simulated}%` :
                           metric.simulated.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: 'var(--space-5)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  <h4 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: 'var(--space-3)' }}>💡 Key Insights</h4>
                  <p style={{ margin: 0, lineHeight: '1.6' }}>
                    Over the next <strong>{simulationParams.timeframe_months} {simulationParams.timeframe_months === 1 ? 'month' : 'months'}</strong>, 
                    your projected revenue would <strong>{simulationResult.changes.revenue >= 0 ? 'increase' : 'decrease'}</strong> by 
                    <strong> ${Math.abs(simulationResult.simulated.revenue - simulationResult.current.revenue).toLocaleString()}</strong> 
                    ({Math.abs(simulationResult.changes.revenue).toFixed(1)}%), while ROI would <strong>{simulationResult.changes.roi >= 0 ? 'improve' : 'decline'}</strong> to 
                    <strong> {simulationResult.simulated.roi}%</strong>. 
                    {simulationResult.changes.revenue > 20 && 'This represents a significant growth opportunity! 🚀'}
                    {simulationResult.changes.revenue < -10 && 'Consider adjusting your strategy to mitigate potential losses. ⚠️'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default DigitalTwin;
